import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Navigate } from 'react-router-dom';

import { api } from '../../../core/network/api';
import { broadcastLogout } from '../../../core/network/session';

// TSS vite/05 — Authentication context. Cookie+CSRF transport.
//
// The JWT lives in an httpOnly cookie set by the backend on /auth/login.
// JS never sees it. csrf_token (the JS-readable companion) is read by
// the api interceptor and echoed in X-CSRF-Token on mutating requests.
//
// This file owns: session state, permission set, permission refresh,
// route guards, and the <Gate> component.
//
// ADAPTED TO THE MASTERMIND BACKEND. The template asked for permissions at
// GET /auth/permissions, which DOES NOT EXIST in mastermind_core. Here the
// session and the permissions come from a single response: GET /auth/me
// returns the UserPackage built by UserAssembler, and that package already
// carries `permissions` expanded from core/authz/roles.js. POST /auth/login
// returns the same package wrapped in { user }.
//
// Practical consequence: refreshing permissions means asking /auth/me again.
// There is no cheaper endpoint and there is no need to invent one.

const EMPTY_SET = new Set();
const AuthContext = createContext(null);

// Holding a permission is NOT string equality — it is a wildcard match.
//
// The backend hands out role bundles verbatim in /auth/me and expands the
// wildcards at evaluation time (core/authz/roles.js + core/authz/permissions.js
// in mastermind_core). A `webmaster` arrives with exactly ['*'], and an
// `owner` with ['public:*']. A plain Set lookup for 'clients:read' misses
// both, so the account with the MOST power is the one that gets bounced to
// /unauthorized — the failure is silent and inverted.
//
// This mirrors `matches()` from core/authz/permissions.js. It is a second copy
// of a rule and copies drift: if that function changes, this one changes with
// it. The alternative — asking the backend per check — is a request per
// rendered button.
function grantMatches(granted, required) {
    if (granted === '*' || granted === required) return true;
    // 'tasks:*' matches 'tasks:read' — the slice keeps the colon.
    if (granted.endsWith(':*')) return required.startsWith(granted.slice(0, -1));
    return false;
}

export function AuthProvider({ children }) {
    const [user, setUser]                 = useState(null);
    const [initializing, setInitializing] = useState(true);
    const [permissions, setPermissions]   = useState(EMPTY_SET);
    const [denied, setDenied]             = useState(EMPTY_SET);
    const [bootstrapError, setBootstrapError] = useState(null);
    const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

    const refreshTimer = useRef(null);

    const applyPermissions = useCallback((payload) => {
        const list   = Array.isArray(payload?.permissions) ? payload.permissions : [];
        const denied = Array.isArray(payload?.denied)      ? payload.denied      : [];
        setPermissions(new Set(list));
        setDenied(new Set(denied));
    }, []);

    const refreshPermissions = useCallback(async () => {
        try {
            const me = await api.get('auth/me').then(r => r.data);
            const account = me?.user || me;
            setUser(account);
            applyPermissions(account);
        } catch {
            // Silent — refresh on 403 is opportunistic.
        }
    }, [applyPermissions]);

    // Bootstrap: a single call to /auth/me. It brings the user and their permissions.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const me = await api.get('auth/me').then(r => r.data);
                if (cancelled) return;
                const account = me?.user || me;
                setUser(account);
                applyPermissions(account);
            } catch (error) {
                if (cancelled) return;
                if (error.status === 401) setUser(null);
                else setBootstrapError(error);
            } finally {
                if (!cancelled) setInitializing(false);
            }
        })();
        return () => { cancelled = true; };
    }, [applyPermissions, bootstrapAttempt]);

    // Refresh on window focus + on the auth:permissions-refresh signal
    // emitted by the api interceptor when a 403 lands.
    useEffect(() => {
        const onVisibility = () => {
            if (user && document.visibilityState === 'visible') {
                clearTimeout(refreshTimer.current);
                refreshTimer.current = setTimeout(refreshPermissions, 250);
            }
        };
        const onRefresh = () => refreshPermissions();

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('auth:permissions-refresh', onRefresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('auth:permissions-refresh', onRefresh);
            clearTimeout(refreshTimer.current);
        };
    }, [refreshPermissions, user]);

    // Multi-tab logout sync — TSS vite/05 §"Logout-everywhere".
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === 'logout_signal') {
                setUser(null);
                setPermissions(EMPTY_SET);
                setDenied(EMPTY_SET);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // credentials = { email, password }. The backend validates against the
    // `login` schema in data/schemas/user.js, which requires email, not username.
    const login = useCallback(async (credentials) => {
        const res = await api.post('auth/login', credentials).then(r => r.data);
        const account = res?.user || res;
        setUser(account);
        applyPermissions(account);
    }, [applyPermissions]);

    const logout = useCallback(async () => {
        // Always clear local state, even if the network call fails.
        try { await api.post('auth/logout'); } catch { /* ignore */ }
        setUser(null);
        setPermissions(EMPTY_SET);
        setDenied(EMPTY_SET);
        broadcastLogout();
    }, []);

    const can = useCallback((domain) => {
        if (!domain) return true;
        // Exact hit first: it is the common case and it is O(1).
        if (permissions.has(domain)) return true;
        for (const granted of permissions) {
            if (grantMatches(granted, domain)) return true;
        }
        return false;
    }, [permissions]);

    // canAny/canAll go through can() and never through permissions.has():
    // otherwise the wildcard rule would hold in one place and not in the other.
    const canAny = useCallback((...domains) => {
        if (!domains.length) return true;
        return domains.some(d => can(d));
    }, [can]);

    const canAll = useCallback((...domains) => {
        if (!domains.length) return true;
        return domains.every(d => can(d));
    }, [can]);

    const value = useMemo(() => ({
        user,
        isAuthenticated: !!user,
        initializing,
        permissions,
        denied,
        can, canAny, canAll,
        refreshPermissions,
        login, logout,
    }), [user, initializing, permissions, denied, can, canAny, canAll, refreshPermissions, login, logout]);

    return <AuthContext.Provider value={value}>{bootstrapError
        ? <SessionRetry error={bootstrapError} onRetry={() => {
            setInitializing(true); setBootstrapError(null); setBootstrapAttempt(n => n + 1);
        }} />
        : children}</AuthContext.Provider>;
}

// A temporary refusal does not mean that the session has expired.
function SessionRetry({ error, onRetry }) {
    const [until] = useState(() => Date.now() + (error.retryAfterMs || 0));
    const [remaining, setRemaining] = useState(() => Math.max(0, until - Date.now()));
    useEffect(() => {
        const timer = setInterval(() => setRemaining(Math.max(0, until - Date.now())), 1000);
        return () => clearInterval(timer);
    }, [until]);
    return <div className="boot-loader" role="alert">
        <p>No pudimos comprobar tu sesión. {error.message}</p>
        <button disabled={remaining > 0} onClick={onRetry}>
            {remaining > 0 ? `Reintentar en ${Math.ceil(remaining / 1000)} s` : 'Reintentar sesión'}
        </button>
    </div>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}

// <Gate> — TSS vite/05 §"UI gating — <Gate> only".
// The ONLY way to conditional-render JSX by permission. Inline can() in
// JSX is forbidden; use can/canAny/canAll only outside JSX.
export function Gate({ domain, any, all, fallback = null, children }) {
    const { can, canAny, canAll } = useAuth();

    let allowed = true;
    if (domain) allowed = can(domain);
    else if (any && any.length) allowed = canAny(...any);
    else if (all && all.length) allowed = canAll(...all);

    return allowed ? <>{children}</> : <>{fallback}</>;
}

// Route guards — TSS vite/05 §"Route guards".

export function ProtectedRoute({ children, domain, any }) {
    const { isAuthenticated, initializing, can, canAny } = useAuth();

    if (initializing) return <BootLoader />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (domain && !can(domain)) return <Navigate to="/unauthorized" replace />;
    if (any && any.length && !canAny(...any)) return <Navigate to="/unauthorized" replace />;
    return children;
}

export function AuthRoute({ children }) {
    const { isAuthenticated, initializing } = useAuth();
    if (initializing) return <BootLoader />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    return children;
}

function BootLoader() {
    return (
        <div className="boot-loader">
            <div className="boot-loader__spinner" />
            <span className="boot-loader__text">Verificando sesión...</span>
        </div>
    );
}
